import {
    ContextNavigationItem,
    NavigationContext,
    RouteActionItems,
    Site,
    SuperbetRouteConfig,
} from '@superbet-group/web.types';
import { Store } from 'vuex';

import { logger } from '@app/logger';
import {
    addFederationRoutes,
    addFederationChildRoutes,
    addAuthenticatedFederationChildRoutes,
} from '@app/router';
import { BetSlipType } from '@core/models/shared/betSlip/betSlipEnums';
import { getCsrfToken } from '@core/services/authentication/helpers';
import ResultsService from '@core/services/results/ResultsService';
import { ensureUserIsOnBetSlipRoute } from '@sportOfferFederation/betSlip/utils/ensureUserIsOnBetSlipRoute';
import { AuthenticationSubscriptionService } from '@store/modules/data/authentication/services/authenticationSubscriptionService';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { createErrorGuardConfig } from '@utils/ErrorGuard/storeConfig';
import { UserService } from '@core/services/user/UserService';
import UserSelectionsContainerFixed from '@modules/sportOffer/UserSelectionsContainerFixed.vue';
import UserSelectionsContainer from '@modules/sportOffer/UserSelectionsContainer.vue';
import { getCurrentSite } from '@src/app/utils/domainHelpers';

// Stays here in the shell, a wrapper around the fed init
export async function initializeTicketsFederation(store: Store<unknown>): Promise<void> {
    try {
        const ticketsModule = await import('tickets/initializeFederation');
        const offerStore = await import('@sportOfferFederation/store/util/trackLiveEvents');

        await ticketsModule.initializeTicketsFederation({
            store,
            errorGuardConfig: createErrorGuardConfig(store),
            routes: {
                addBasicRoutes: addFederationRoutes,
                addChildRoutes: addFederationChildRoutes,
                addLottoBetslipRoutes,
                addSportBetslipRoutes,
                addAuthenticatedChildRoutes: addAuthenticatedFederationChildRoutes,
                addDesktopNavigationItem: (key: NavigationContext, value: ContextNavigationItem) => {
                    store.dispatch('navigation/addDesktopNavigationItem', { key, value });
                },
                addMobileNavigationItem: (key: NavigationContext, value: ContextNavigationItem) => {
                    store.dispatch('navigation/addMobileNavigationItem', { key, value });
                },
                addMobileRouteActionItems: (routeActionItems: RouteActionItems) => {
                    store.dispatch('navigation/addMobileRouteActionItems', routeActionItems);
                },
            },
            authentication: {
                registerPostLoginHandler: AuthenticationSubscriptionService.registerPostLoginHandler,
                registerPostLogoutHandler: AuthenticationSubscriptionService.registerPostLogoutHandler,
            },
            offer: {
                trackLiveOfferEvents: offerStore.useLiveEventTracking(store).trackLiveOfferEvents,
            },
            betting: {
                isBettingDisabled: getCurrentSite(window.location.hostname) === Site.SuperSocialBr,
            },
            services: {
                getMatchResults: (eventIds: number[]) =>
                    ResultsService.getInstance().getMatchResultDetails(eventIds),
                getCsrfToken,
                ensureBetSlipRoute: ensureUserIsOnBetSlipRoute,
                openBetslip: (hasSystems: boolean) => {
                    store.dispatch('ui/modal/setModal', {
                        code: modalCodes.mobileBetSlip,
                    }, { root: true });
                    store.dispatch('ui/sportOffer/betSlip/setType', hasSystems ? BetSlipType.system : BetSlipType.simple, { root: true });
                },
                getUserLicencePreferences: () =>
                    UserService.getInstance().getUserLicencePreferences(),
            },
            experiments: {
                isNewSportsNavigationEnabled: true,
                isNewTopLevelNavigationEnabled: true,
            },
        });
    } catch (error) {
        logger.error(error as Error);
    }
}

function addSportBetslipRoutes(routes: SuperbetRouteConfig[]) {
    addFederationRoutes(routes.map((route) => ({
        ...route,
        components: {
            ...route.components!,
            rightSidebar: UserSelectionsContainerFixed,
            mobileBottomContent: UserSelectionsContainer,
        },
    })));
}

// TODO: Remove after all tickets changes are in production
function addLottoBetslipRoutes(): void {}
